import type { User } from "schemas/generated/user";

import * as React from "react";

import { Avatar } from "@replo/design-system/components/avatar/Avatar";
import { Badge } from "@replo/design-system/components/badge/Badge";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@replo/design-system/components/sidebar/Sidebar";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLocalStorage } from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { authRouter } from "@v2/trpc/routes/auth";
import { routes } from "@v2/utils/router";
import { storeToken } from "@v2/utils/store-token";
import { ChevronsUpDown, Coins, LogOut, UserIcon } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

const NavUserHeader = ({ user }: { user: User }) => {
  return (
    <SidebarMenuButton
      size="lg"
      className="data-[state=open]:bg-menu-item-selected data-[state=open]:text-default items-start"
    >
      <div className="flex gap-2 justify-center items-center h-full">
        <Badge size="xl">
          <Avatar name={user.name} isSquare />
        </Badge>

        <div className="grid flex-1 text-left text-sm leading-tight">
          <span className="truncate header-h6 line-clamp-1">{user.name}</span>

          <span className="truncate text-xs text-muted line-clamp-1">
            {user.email}
          </span>
        </div>
      </div>
      <ChevronsUpDown className="ml-auto size-3! mt-1" />
    </SidebarMenuButton>
  );
};

export function NavUser() {
  const { isMobile } = useSidebar();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const localStorage = useLocalStorage();
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  const logEvent = useLogAnalytics();

  const { mutateAsync: logout } = authRouter.logout.useMutation({
    onSuccess: async () => {
      storeToken(null);
      window.location.replace("/auth/login");
    },
  });

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Menu>
          <MenuTrigger className="w-full">
            {user ? (
              <NavUserHeader user={user} />
            ) : (
              <Skeleton className="w-full h-12" />
            )}
          </MenuTrigger>

          <MenuContent
            align="end"
            alignOffset={isMobile ? 0 : -12}
            side={isMobile ? "bottom" : "right"}
            sideOffset={isMobile ? 0 : 16}
          >
            <MenuItem
              label="My Account"
              startEnhancer={<UserIcon />}
              onClick={() => {
                logEvent("dashboard.nav.click", {
                  tab: "your_profile",
                  userId: user?.id ?? null,
                  workspaceId: workspaceId ?? null,
                });
                navigate(
                  generatePath(routes.shops.settings.profile, {
                    projectId,
                  }),
                );
              }}
            />
            <MenuItem
              label="My Referrals"
              startEnhancer={<Coins />}
              onClick={() => {
                logEvent("dashboard.nav.click", {
                  tab: "my_referrals",
                  userId: user?.id ?? null,
                  workspaceId: workspaceId ?? null,
                });
                navigate(
                  generatePath(routes.shops.settings.referrals, {
                    projectId,
                  }),
                );
              }}
            />
            <MenuSeparator />
            <MenuItem
              label="Log out"
              startEnhancer={<LogOut />}
              onClick={() => {
                logEvent("dashboard.logout", {});
                void logout({});

                // Note (Noah, 2025-02-22): Make sure to clear the last visited
                // workspace when logging out, otherwise when you log in again to
                // a new account you'll see a permissions error by default
                localStorage.removeItem(
                  "replo.workspace.lastvisitedWorkspaceId",
                );
              }}
            />
          </MenuContent>
        </Menu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
